import React, { useState } from "react";
import { useLang } from "@/lib/i18n";
import { usePlan } from "@/context/PlanContext";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { downloadReport, shareWhatsApp } from "@/lib/download";
import { formatCurrency, isoDate, MONTH_NAMES } from "@/lib/format";
import ResultDisplay from "@/components/ResultDisplay";
import MDCFForm from "@/components/MDCFForm";
import CustomReportsNotice from "@/components/CustomReportsNotice";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, Share2, Eye, CalendarRange, CalendarDays, CalendarClock, ClipboardList, Wheat, Lock } from "lucide-react";

const TYPES = [
  { key: "daily", labelKey: "daily_report", icon: CalendarDays, param: "date" },
  { key: "weekly", labelKey: "weekly_report", icon: CalendarRange, param: "date" },
  { key: "monthly", labelKey: "monthly_report", icon: CalendarClock, param: "month" },
  { key: "mdcf", labelKey: "mdcf_form", icon: ClipboardList, param: "month" },
  { key: "dry_ration", labelKey: "dry_ration_report", icon: Wheat, param: "month" },
  { key: "annual", labelKey: "annual_report", icon: CalendarClock, param: "year" },
];

// Reports that need an active subscription (daily stays open for trial users).
const LOCKED_TYPES = new Set(["weekly", "monthly", "mdcf", "dry_ration", "annual", "uc"]);

export default function Reports() {
  const { t } = useLang();
  const { handle402, hasAccess, openUpgrade } = usePlan();
  const now = new Date();
  const [type, setType] = useState("daily");
  const [date, setDate] = useState(isoDate(now));
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const cfg = TYPES.find((x) => x.key === type);
  const params = () => (cfg.param === "date" ? { d: date } : cfg.param === "month" ? { year, month } : { year });

  const locked = (k) => LOCKED_TYPES.has(k) && !hasAccess;

  const selectType = (k) => {
    setType(k); setData(null);
    if (locked(k)) openUpgrade();   // open the Upgrade sheet as soon as a locked report is tapped
  };

  const view = async () => {
    if (locked(type)) { openUpgrade(); return; }   // pre-check before hitting the API
    setLoading(true); setData(null);
    try {
      const { data: d } = await api.get(`/reports/${type}`, { params: cfg.param === "date" ? { d: date } : cfg.param === "month" ? { year, month } : { year } });
      setData(d);
      track("report_viewed", { type });
    } catch (e) { if (!handle402(e)) toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doDownload = async (fmt) => {
    setBusy(fmt);
    try { await downloadReport(type, fmt, params()); toast.success(`${fmt === "excel" ? "Excel" : "PDF"} downloaded`); }
    catch (e) { if (!handle402(e)) toast.error("Download failed. Save an entry first."); }
    finally { setBusy(""); }
  };

  const doShare = async () => {
    setBusy("wa");
    const summary = buildSummary(type, data, { date, month, year });
    try { await shareWhatsApp(type, params(), summary); track("whatsapp_share", { type }); }
    catch (e) { if (!handle402(e)) toast.error("Sharing failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-5 pb-24">
      <h1 className="text-2xl md:text-3xl font-heading font-extrabold">{t("reports")}</h1>
      <CustomReportsNotice />

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {TYPES.map((x) => (
          <button key={x.key} onClick={() => selectType(x.key)} data-testid={`report-type-${x.key}`}
            className={`relative rounded-xl p-3 text-xs font-semibold border transition-all flex flex-col items-center gap-1 ${type === x.key ? "bg-thali-green text-white border-thali-green shadow-btn-green" : "bg-thali-paper border-thali-turmeric/30 text-thali-ink/70"}`}>
            {locked(x.key) && <Lock className={`absolute top-1.5 right-1.5 w-3.5 h-3.5 ${type === x.key ? "text-white/80" : "text-thali-saffron"}`} data-testid={`report-lock-${x.key}`} />}
            <x.icon className="w-5 h-5" /> {x.key === "mdcf" ? "MDCF Form" : t(x.labelKey)}
          </button>
        ))}
      </div>

      {type === "mdcf" ? (
        locked("mdcf") ? (
          <div className="card-thali text-center space-y-3 py-8" data-testid="mdcf-locked">
            <Lock className="w-10 h-10 text-thali-saffron mx-auto" />
            <div className="font-heading font-bold text-lg">{t("upgrade_title")}</div>
            <p className="text-sm text-thali-ink/70">{t("upgrade_sub")}</p>
            <button onClick={() => openUpgrade()} className="btn-3d-green" data-testid="mdcf-locked-upgrade">{t("subscribe_now")}</button>
          </div>
        ) : (
        <>
          <div className="card-thali flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[120px]">
              <label className="text-sm font-semibold">Month</label>
              <select className="input-thali mt-1" value={month} onChange={(e) => setMonth(+e.target.value)} data-testid="report-month">
                {MONTH_NAMES.slice(1).map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="w-28">
              <label className="text-sm font-semibold">Year</label>
              <input type="number" className="input-thali mt-1" value={year} onChange={(e) => setYear(+e.target.value)} data-testid="report-year" />
            </div>
          </div>
          <MDCFForm year={year} month={month} />
        </>
        )
      ) : (
      <>
      <div className="card-thali flex flex-wrap items-end gap-3">
        {cfg.param === "date" && (
          <div className="flex-1 min-w-[150px]">
            <label className="text-sm font-semibold">{t("date")}</label>
            <input type="date" className="input-thali mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="report-date" />
          </div>
        )}
        {cfg.param === "month" && (
          <>
            <div className="flex-1 min-w-[120px]">
              <label className="text-sm font-semibold">Month</label>
              <select className="input-thali mt-1" value={month} onChange={(e) => setMonth(+e.target.value)} data-testid="report-month">
                {MONTH_NAMES.slice(1).map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="w-28">
              <label className="text-sm font-semibold">Year</label>
              <input type="number" className="input-thali mt-1" value={year} onChange={(e) => setYear(+e.target.value)} data-testid="report-year" />
            </div>
          </>
        )}
        {cfg.param === "year" && (
          <div className="w-32">
            <label className="text-sm font-semibold">Year</label>
            <input type="number" className="input-thali mt-1" value={year} onChange={(e) => setYear(+e.target.value)} data-testid="report-year" />
          </div>
        )}
        <button onClick={view} className="btn-3d-green" data-testid="report-view"><Eye className="w-4 h-4" /> {t("view")}</button>
      </div>

      {loading && <div className="text-center py-8 text-thali-ink/50">Loading…</div>}

      {data && (
        <div data-testid="report-content">
          {type === "daily" ? <ResultDisplay result={data.report} /> : <SummaryReport type={type} data={data} />}
        </div>
      )}

      {data && (
        <div className="fixed bottom-[60px] md:bottom-0 inset-x-0 md:left-64 bg-thali-paper border-t border-thali-turmeric/20 p-3 flex gap-2 z-30 justify-center shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
          <button onClick={() => doDownload("pdf")} disabled={busy} className="btn-3d-saffron flex-1 max-w-[160px]" data-testid="report-pdf"><FileText className="w-4 h-4" /> {t("export_pdf")}</button>
          <button onClick={() => doDownload("excel")} disabled={busy} className="btn-3d-green flex-1 max-w-[160px]" data-testid="report-excel"><FileSpreadsheet className="w-4 h-4" /> {t("export_excel")}</button>
          <button onClick={doShare} disabled={busy} className="btn-3d-turmeric flex-1 max-w-[160px]" data-testid="report-whatsapp"><Share2 className="w-4 h-4" /> {t("share_whatsapp")}</button>
        </div>
      )}
      </>
      )}
    </div>
  );
}

const CAT_LABEL = { balvatika: "Balvatika", primary: "Primary", upper_primary: "Upper Primary", high_school: "High School" };
const DT_TAG = { mdm: { label: "MDM", cls: "bg-thali-green/15 text-thali-green" }, dry_ration: { label: "Dry Ration", cls: "bg-thali-turmeric/30 text-thali-turmericdark" }, holiday: { label: "Holiday", cls: "bg-thali-ink/10 text-thali-ink/60" } };

function DayTypeBadge({ dt }) {
  const c = DT_TAG[dt] || DT_TAG.mdm;
  return <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${c.cls}`}>{c.label}</span>;
}

function DryRationReport({ data }) {
  const t2 = data.totals || {};
  const cards = [
    { label: "Dry Ration Days", value: t2.days },
    { label: "Total Amount", value: t2.amount_fmt },
    { label: "🏦 DBT", value: t2.dbt_amount_fmt },
    { label: "💵 Cash", value: t2.cash_amount_fmt },
    { label: "Total Rice", value: `${t2.rice} kg` },
  ];
  return (
    <div className="space-y-4" data-testid="dry-ration-report">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {cards.map((c, i) => (
          <div key={i} className="card-thali p-3 text-center">
            <div className="text-xs text-thali-ink/60">{c.label}</div>
            <div className="font-num font-bold text-lg text-thali-green">{c.value ?? "—"}</div>
          </div>
        ))}
      </div>
      <div className="card-thali overflow-hidden p-0">
        <div className="px-4 py-3 bg-thali-green text-white font-heading font-bold">🌾 Per-class Entitlement</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">Category</th><th className="p-2">Students</th>{data.per_category.some((p) => p.cost_per_student_fmt) && <th className="p-2">Cost/Student</th>}<th className="p-2">Amount</th><th className="p-2">Rice/Student</th><th className="p-2 pr-4 text-right">Total Rice</th></tr></thead>
          <tbody>
            {data.per_category.map((p) => (
              <tr key={p.cat} className="border-t border-thali-turmeric/10"><td className="p-2 pl-4 font-medium">{CAT_LABEL[p.cat]}</td><td className="p-2 text-center">{p.students}</td>{p.cost_per_student_fmt && <td className="p-2 text-center">{p.cost_per_student_fmt}</td>}<td className="p-2 text-center">{p.amount_fmt}</td><td className="p-2 text-center">{p.rice_per_student_g} g</td><td className="p-2 pr-4 text-right">{p.rice} kg</td></tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-2 text-xs text-thali-ink/50">Amount is paid as dry ration (DBT/Cash); rice is issued in kind. Not part of cooking cost.</div>
      </div>
      {data.days.length > 0 ? (
        <div className="card-thali overflow-hidden p-0">
          <div className="px-4 py-3 bg-thali-ink text-white font-heading font-bold">Day-wise Dry Ration</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">Date</th><th className="p-2">Mode</th><th className="p-2">Students</th><th className="p-2">Amount</th><th className="p-2 pr-4 text-right">Rice</th></tr></thead>
            <tbody>{data.days.map((d) => (<tr key={d.date} className="border-t border-thali-turmeric/10"><td className="p-2 pl-4">{d.date_fmt} {d.weekday ? `(${d.weekday})` : ""}</td><td className="p-2 text-center">{d.payment_mode === "cash" ? "💵 Cash" : "🏦 DBT"}</td><td className="p-2 text-center">{d.students}</td><td className="p-2 text-center font-semibold">{d.amount_fmt}</td><td className="p-2 pr-4 text-right">{d.rice} kg</td></tr>))}</tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-thali-ink/50" data-testid="report-empty">No dry-ration days saved for this month.</div>
      )}
    </div>
  );
}

function SummaryReport({ type, data }) {
  const { t } = useLang();
  if (type === "dry_ration") return <DryRationReport data={data} />;
  const tot = data.totals || {};
  const bd = data.breakdown;
  const summaryCards = bd ? [
    { label: t("working_days"), value: bd.working_days },
    { label: t("cooking_cost"), value: bd.cooking_cost_fmt },
    { label: t("dry_ration_amount"), value: bd.dry_amount_fmt },
    { label: t("total_payout"), value: bd.total_payout_fmt },
    { label: t("rice_kg"), value: `${bd.total_rice} kg` },
  ] : [
    { label: t("days_entered"), value: tot.days_entered },
    { label: t("total_cost"), value: tot.cost_fmt },
    { label: t("rice_kg"), value: `${tot.rice} kg` },
    { label: "Meals", value: tot.meals },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {summaryCards.map((c, i) => (
          <div key={i} className="card-thali p-3 text-center">
            <div className="text-xs text-thali-ink/60">{c.label}</div>
            <div className="font-num font-bold text-lg text-thali-green">{c.value ?? "—"}</div>
          </div>
        ))}
      </div>

      {bd && (
        <div className="card-thali overflow-hidden p-0" data-testid="report-daytype-breakdown">
          <div className="px-4 py-3 bg-thali-turmeric/90 text-thali-ink font-heading font-bold">🍽️ Day-type Breakdown</div>
          <div className="grid grid-cols-3 divide-x divide-thali-turmeric/20 text-center">
            <div className="p-3"><div className="text-xs text-thali-ink/60">MDM Served</div><div className="font-num font-bold text-thali-green">{bd.mdm_days}</div></div>
            <div className="p-3"><div className="text-xs text-thali-ink/60">Dry Ration</div><div className="font-num font-bold text-thali-turmericdark">{bd.dry_days}</div></div>
            <div className="p-3"><div className="text-xs text-thali-ink/60">Holidays</div><div className="font-num font-bold text-thali-ink/60">{bd.holiday_days}</div></div>
          </div>
          <div className="px-4 py-2 text-xs text-thali-ink/60 border-t border-thali-turmeric/20">
            Dry ration: {bd.dry_amount_fmt} (🏦 {bd.dry_dbt_fmt} · 💵 {bd.dry_cash_fmt}) · of which rice {bd.dry_rice} kg — kept separate from cooking cost.
          </div>
        </div>
      )}

      {(type === "monthly" || type === "uc") && data.per_category && (
        <div className="card-thali overflow-hidden p-0">
          <div className="px-4 py-3 bg-thali-green text-white font-heading font-bold">Category-wise Cooking Cost (MDM served)</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">Category</th><th className="p-2">Meal-days</th><th className="p-2">Cost</th><th className="p-2 pr-4 text-right">Rice</th></tr></thead>
            <tbody>
              {Object.entries(data.per_category).map(([c, v]) => (
                <tr key={c} className="border-t border-thali-turmeric/10"><td className="p-2 pl-4 font-medium">{CAT_LABEL[c]}</td><td className="p-2 text-center">{v.meal_days}</td><td className="p-2 text-center">{formatCurrency(v.cost)}</td><td className="p-2 pr-4 text-right">{Math.round(v.rice * 100) / 100} kg</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.days && data.days.length > 0 && (
        <div className="card-thali overflow-hidden p-0">
          <div className="px-4 py-3 bg-thali-ink text-white font-heading font-bold">Day-wise</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">Date</th><th className="p-2">Type</th><th className="p-2">Students</th><th className="p-2 pr-4 text-right">Amount</th></tr></thead>
            <tbody>{data.days.map((d) => (<tr key={d.date} className="border-t border-thali-turmeric/10"><td className="p-2 pl-4">{d.date_fmt} {d.weekday ? `(${d.weekday})` : ""}</td><td className="p-2 text-center"><DayTypeBadge dt={d.day_type || "mdm"} /></td><td className="p-2 text-center">{d.students}</td><td className="p-2 pr-4 text-right font-semibold">{d.cost_fmt}</td></tr>))}</tbody>
          </table>
        </div>
      )}

      {type === "annual" && data.months && (
        <div className="card-thali overflow-hidden p-0">
          <div className="px-4 py-3 bg-thali-ink text-white font-heading font-bold">Month-wise</div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">Month</th><th className="p-2">MDM</th><th className="p-2">Dry</th><th className="p-2">Meals</th><th className="p-2">Cooking</th><th className="p-2 pr-4 text-right">Dry Amt</th></tr></thead>
            <tbody>{data.months.map((m) => (<tr key={m.month} className="border-t border-thali-turmeric/10"><td className="p-2 pl-4">{MONTH_NAMES[m.month]}</td><td className="p-2 text-center">{m.mdm_days}</td><td className="p-2 text-center">{m.dry_days}</td><td className="p-2 text-center">{m.meals}</td><td className="p-2 text-center">{m.cost_fmt}</td><td className="p-2 pr-4 text-right font-semibold">{formatCurrency(m.dry_amount)}</td></tr>))}</tbody>
          </table>
          </div>
        </div>
      )}

      {bd && bd.working_days === 0 && (
        <div className="text-center py-8 text-thali-ink/50" data-testid="report-empty">No saved entries for this period. Save some daily entries first.</div>
      )}
      {!bd && (!tot.days_entered || tot.days_entered === 0) && (
        <div className="text-center py-8 text-thali-ink/50" data-testid="report-empty">No saved entries for this period. Save some daily entries first.</div>
      )}
    </div>
  );
}

function buildSummary(type, data, sel) {
  if (!data) return "PM POSHAN MDM Report";
  if (type === "daily") {
    const s = data.report.summary;
    return `PM POSHAN Daily Report ${data.report.weekday_name}\nStudents: ${s.total_students}\nTotal: ${formatCurrency(s.grand_total)}\nRice: ${s.total_rice_kg} kg\nvia pmposhanmdmcalculator.in`;
  }
  if (type === "dry_ration") {
    const tt = data.totals || {};
    return `PM POSHAN Dry Ration Report\nDays: ${tt.days}\nAmount: ${tt.amount_fmt} (DBT ${tt.dbt_amount_fmt} · Cash ${tt.cash_amount_fmt})\nRice: ${tt.rice} kg\nvia pmposhanmdmcalculator.in`;
  }
  const bd = data.breakdown;
  if (bd) {
    return `PM POSHAN ${type.toUpperCase()} Report\nMDM ${bd.mdm_days} · Dry ${bd.dry_days} · Holiday ${bd.holiday_days}\nCooking Cost: ${bd.cooking_cost_fmt}\nDry Ration: ${bd.dry_amount_fmt}\nTotal Payout: ${bd.total_payout_fmt}\nRice: ${bd.total_rice} kg\nvia pmposhanmdmcalculator.in`;
  }
  const t = data.totals || {};
  return `PM POSHAN ${type.toUpperCase()} Report\nDays: ${t.days_entered}\nTotal Cost: ${t.cost_fmt}\nRice: ${t.rice} kg\nMeals: ${t.meals}\nvia pmposhanmdmcalculator.in`;
}
